import React from "react";
import { Pressable, View } from "react-native";
import StyledText from "../../common/ui/styled-text";
import { CreditCard, Landmark, SquareMinus } from "lucide-react-native";
import type { StripePaymentMethod } from "../../../types/api/billing";

export function extractPaymentMethodDetails(pm?: StripePaymentMethod | null): {
  accountType: string;
  shortDisplay: string;
  longDisplay: string;
  subtext: string;
} {
  const type = (pm?.type || "").toLowerCase();

  switch (type) {
    case "card": {
      const brand = pm?.card?.brand || "";
      const last4 = pm?.card?.last4 || "";
      const exp =
        pm?.card?.exp_month && pm?.card?.exp_year
          ? `${pm.card.exp_month}/${String(pm.card.exp_year).slice(-2)}`
          : "";
      const shortDisplay = last4 ? `•••• ${last4}` : "—";
      const longDisplay = last4 ? `•••• •••• •••• ${last4}` : "—";
      const subtext = brand
        ? `${brand.toUpperCase()}${exp ? ` Expires ${exp}` : ""}`
        : exp
          ? `Expires ${exp}`
          : "";
      return {
        accountType: "card",
        shortDisplay,
        longDisplay,
        subtext,
      };
    }
    case "us_bank_account": {
      const last4 = pm?.us_bank_account?.last4 || "";
      const bank = pm?.us_bank_account?.bank_name || "";
      const acctType = pm?.us_bank_account?.account_type || "";
      const shortDisplay = last4 ? `•••• ${last4}` : "—";
      const longDisplay = last4 ? `•••••••••••• ${last4}` : "—";
      const prettyAcct = acctType
        ? acctType.charAt(0).toUpperCase() + acctType.slice(1)
        : "";
      const subtext = bank || prettyAcct || "";
      return {
        accountType: "us_bank_account",
        shortDisplay,
        longDisplay,
        subtext,
      };
    }
    default: {
      return {
        accountType: type || "unknown",
        shortDisplay: "—",
        longDisplay: "—",
        subtext: "",
      };
    }
  }
}

export function PaymentMethodIcon({
  type,
  color = "#fff",
  size = 14,
}: {
  type: string;
  color?: string;
  size?: number;
}) {
  if (type === "card") {
    return <CreditCard size={size} color={color} />;
  }
  if (type === "us_bank_account") {
    return <Landmark size={size} color={color} />;
  }
  return <SquareMinus size={size} color={color} />;
}

interface PaymentMethodProps {
  paymentMethod?: StripePaymentMethod | null;
  onUpdate: () => void;
}

const PaymentMethod: React.FC<PaymentMethodProps> = ({
  paymentMethod,
  onUpdate,
}) => {
  const { accountType, longDisplay, subtext } =
    extractPaymentMethodDetails(paymentMethod);

  return (
    <View className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      <StyledText className="text-sm font-medium text-gray-900 mb-3">
        Payment Method
      </StyledText>
      <View className="flex-row items-center justify-between">
        {/* Left: brand+digits */}
        <View className="flex-1 pr-3">
          <View className="flex-row items-center gap-2">
            <View className="w-6 h-6 rounded-md bg-blue-600 items-center justify-center">
              <PaymentMethodIcon type={accountType} color="#fff" />
            </View>
            <StyledText className="text-base font-medium text-gray-900">
              {longDisplay || "—"}
            </StyledText>
          </View>
          <StyledText className="text-[12px] text-gray-600 mt-1">
            {subtext || "No details on file"}
          </StyledText>
        </View>
        {/* Right: actions inside the container */}
        <View className="flex-row items-center gap-3">
          <Pressable
            onPress={onUpdate}
            className="px-3 py-1.5 rounded-md border border-gray-300 bg-white"
          >
            <StyledText className="text-sm text-gray-900">Update</StyledText>
          </Pressable>
        </View>
      </View>
    </View>
  );
};

export default PaymentMethod;
